/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { Plan, Pack } from "./plan.type";
import { useLocationState } from "../../Utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";


export const PlanApi = createApi({
    reducerPath: "planApi",
    tagTypes: ["plan", "plans", "packs", "pack"],
    baseQuery: fakeBaseQuery(),
    endpoints: (builder) => ({
        addOrEditPlan: builder.mutation({
            async queryFn({ id, ...data }) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    if (!id) {
                        const docRef = await addDoc(collection(db, "plans"), { ...data, createdAt: serverTimestamp(), createdBy: userId, updatedAt: serverTimestamp() });
                        return {
                            data: {
                                id: docRef.id,
                                message: "Plan ajouté.",
                                ...data,
                            },
                        };

                    } else {
                        // Mettre à jour Firestore
                        const docRef = doc(db, "plans", id);
                        await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });

                        return {
                            data: {
                                success: true,
                                message:
                                    "Plan modifié.",
                            },
                        };
                    }
                } catch (error: any) {
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["plan", "plans"],
        }),
        getPlans: builder.query({
            queryFn: async ({ isActive }) => {
                const plansRef = collection(db, "plans");
                let q = query(plansRef, orderBy("price", "asc"));
                if (isActive) {
                    q = query(
                        plansRef,
                        where("isActive", "==", isActive),
                        orderBy("price", "asc")
                    );
                }
                const snapshot = await getDocs(q);

                const plans = await Promise.all(
                    snapshot.docs.map(async (docSnap) => {
                        const plan = docSnap.data();

                        return {
                            id: docSnap.id,
                            ...plan
                        };
                    })
                );

                return { data: plans as Plan[] };
            },
            // Gestion du temps réel avec Firestore et `onSnapshot`
            async onCacheEntryAdded(
                { isActive },
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                let unsubscribe;

                try {
                    await cacheDataLoaded;

                    const plansRef = collection(db, "plans");
                    let q = query(plansRef, orderBy("price", "asc"));
                    if (isActive) {
                        q = query(
                            plansRef,
                            where("isActive", "==", isActive),
                            orderBy("price", "asc")
                        );
                    }
                    unsubscribe = onSnapshot(q, async (snapshot) => {
                        const plans = await Promise.all(
                            snapshot.docs.map(async (docSnap) => {
                                const plan = docSnap.data();

                                return {
                                    id: docSnap.id,
                                    ...plan
                                };
                            })
                        );

                        // Met à jour le cache RTK Query avec les nouvelles données
                        updateCachedData(() => plans as Plan[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["plans"],
        }),
        getAllPlans: builder.query({
            async queryFn() {
                try {
                    return new Promise((resolve) => {
                        const q = query(
                            collection(db, "plans"),
                            orderBy("price", "asc"),
                        );
                        onSnapshot(q, (snapshot) => {
                            const plans = snapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));
                            resolve({ data: plans as Plan[] });
                        });
                        // unsubscribe();
                    });
                } catch (error) {
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const plansRef = query(
                    collection(db, "plans"),
                    orderBy("price", "asc"),
                );
                let unsubscribe;
                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(plansRef, (snapshot) => {
                        const plans = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        }));
                        updateCachedData(() => plans as Plan[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["plans"],
        }),
        getPlanById: builder.query({
            queryFn: async (planId) => {
                try {
                    const planRef = doc(db, "plans", planId);
                    const plansnap = await getDoc(planRef);

                    if (!plansnap.exists()) {
                        return {
                            error: { status: 404, message: "Matière introuvable !" },
                        };
                    }

                    return { data: { id: plansnap.id, ...plansnap.data() } };
                } catch (error: any) {
                    return { error: { status: 500, message: error.message } };
                }
            },
            providesTags: ["plan"],
        }),
        // Supprimer
        deletePlan: builder.mutation({
            queryFn: async (uid) => {
                try {
                    const docRef = doc(db, "plans", uid);
                    await deleteDoc(docRef);
                    return {
                        data: { uid, message: "Plan supprimé.", success: true },
                    };
                } catch (error: any) {
                    return { error: { status: "DELETE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["plan", "plans"],
        }),
        addOrEditPack: builder.mutation({
            async queryFn({ id, ...data }) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    if (!id) {
                        const docRef = await addDoc(collection(db, "packs"), { ...data, createdAt: serverTimestamp(), createdBy: userId, updatedAt: serverTimestamp() });
                        return {
                            data: {
                                id: docRef.id,
                                message: "Pack ajouté.",
                                ...data,
                            },
                        };

                    } else {
                        // Mettre à jour Firestore
                        const docRef = doc(db, "packs", id);
                        await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });

                        return {
                            data: {
                                success: true,
                                message:
                                    "Pack modifié.",
                            },
                        };
                    }
                } catch (error: any) {
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["pack", "packs"],
        }),
        getPacks: builder.query({
            queryFn: async ({ isActive }) => {
                const packsRef = collection(db, "packs");
                let q = query(packsRef, orderBy("price", "asc"));
                if (isActive) {
                    q = query(
                        packsRef,
                        where("isActive", "==", isActive),
                        orderBy("price", "asc")
                    );
                }
                const snapshot = await getDocs(q);

                const packs = await Promise.all(
                    snapshot.docs.map(async (docSnap) => {
                        const pack = docSnap.data();

                        return {
                            id: docSnap.id,
                            ...pack
                        };
                    })
                );

                return { data: packs as Pack[] };
            },
            // Gestion du temps réel avec Firestore et `onSnapshot`
            async onCacheEntryAdded(
                { isActive },
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                let unsubscribe;

                try {
                    await cacheDataLoaded;

                    const packsRef = collection(db, "packs");
                    let q = query(packsRef, orderBy("price", "asc"));
                    if (isActive) {
                        q = query(
                            packsRef,
                            where("isActive", "==", isActive),
                            orderBy("price", "asc")
                        );
                    }
                    unsubscribe = onSnapshot(q, async (snapshot) => {
                        const packs = await Promise.all(
                            snapshot.docs.map(async (docSnap) => {
                                const pack = docSnap.data();

                                return {
                                    id: docSnap.id,
                                    ...pack
                                };
                            })
                        );

                        // Met à jour le cache RTK Query avec les nouvelles données
                        updateCachedData(() => packs as Pack[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["packs"],
        }),
        deletePack: builder.mutation({
            queryFn: async (uid) => {
                try {
                    const docRef = doc(db, "packs", uid);
                    await deleteDoc(docRef);
                    return {
                        data: { uid, message: "Pack supprimé.", success: true },
                    };
                } catch (error: any) {
                    return { error: { status: "DELETE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["pack", "packs"],
        }),
    }),
});

export const {
    useAddOrEditPlanMutation,
    useGetPlansQuery,
    useGetAllPlansQuery,
    useDeletePlanMutation,
    useLazyGetPlanByIdQuery,
    useAddOrEditPackMutation,
    useGetPacksQuery,
    useDeletePackMutation
} = PlanApi;

export function usePlanFromLocation(): [
    Plan,
    boolean,
    string,
    (id: string) => any
] {
    const itemState = useLocationState<Plan | undefined>(undefined);
    const [item, setItem] = useState(itemState);
    const { id } = useParams<{ id: string }>();
    const [findById, { data, isError, isLoading }] = useLazyGetPlanByIdQuery();
    const navigate = useNavigate();

    useEffect(() => {
        // console.log("is id", id)
        if (id && id !== undefined) {
            // console.log("id", id)
            findById(id as string);
        }
    }, [id]);
    useEffect(() => {
        if (isError && !itemState) {
            navigate(-1);
        }
    }, [isError]);
    useEffect(() => {
        if (data) {
            setItem(data as Plan);
        }
    }, [data]);

    return [item as Plan & { bio: string }, isLoading, id as string, findById];
}